/**
 * Settings → Planning → Type lists & patterns (migration 918;
 * docs/9-exploration/TYPE-PATTERNS-PRD.md).
 *
 * Each department classifies items its own way — a type list. Each type may
 * carry a pattern: Certainty, Shape, Workload, Bar. Admins add lists and name
 * each list's owners (the people under an org unit); owners, admins and anyone
 * whose role carries type_lists.edit (migration 931) edit a
 * list's types and patterns. A control is drawn only for someone who may use it
 * (build contract §2.8); the database checks again.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layers, Pencil, Plus } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { PatternEditor } from '../../../components/prioritization/typePatterns/PatternEditor';
import { Field, Select, TextInput } from '../../../components/ds';
import { btnPrimary, btnSecondary } from '../../../components/mono/primitives';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTypePatternsStore } from '../../../stores/prioritization/typePatternsStore';
import { useResourceTypesStore } from '../../../stores/prioritization/resourceTypesStore';
import { useEnterpriseIdentityStore, useOrgUnits } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTypeListPickers, type TypeListWithOptions } from '../../../hooks/prioritization/useTypeListPickers';
import { formatOrgUnitPathLabel } from '../../../lib/orgUnitTree';
import { getLaunchProfiles } from '../../../lib/timePhasedPlanningService';
import type { LaunchProfile } from '../../../lib/timePhasedPlanning.types';
import { patternSummary } from '../../../lib/typePatterns';
import { mayEditTypeList, patternPolicyBlock, saveCustomType, saveTypeList } from '../../../lib/typePatternsService';
import type { TypeOption } from '../../../types/prioritization/typePatterns';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';

const BUILTIN_PAGE: Record<string, { href: string; label: string }> = {
  work_type: { href: '/prioritization/settings/work-types', label: 'Work Types' },
  launch_type: { href: '/prioritization/settings/launch-types', label: 'Launch Types' },
  investment_type: { href: '/prioritization/settings/investment-types', label: 'Investment Types' },
};

// ── One list ──────────────────────────────────────────────────────────────────

function TypeListCard({
  list,
  isAdmin,
  mayEdit,
  patternBlock,
  orgUnits,
  orgLabel,
  workTypeFacts,
  resourceTypes,
  workloadTemplates,
}: {
  list: TypeListWithOptions;
  isAdmin: boolean;
  /** Undefined until the database has answered; nothing is said until then. */
  mayEdit: boolean | undefined;
  /** A policy's plain reason when one holds back pattern changes for this person; else null. */
  patternBlock: string | null;
  orgUnits: OrgUnit[];
  orgLabel: (id: string | null) => string | null;
  workTypeFacts: (v: string) => { approach?: string | null; effortMultiplier?: number | null } | null;
  resourceTypes: Array<{ key: string; label: string }>;
  workloadTemplates: LaunchProfile[];
}) {
  const patternFor = useTypePatternsStore((s) => s.patternFor);
  useTypePatternsStore((s) => s.patterns);
  const putPattern = useTypePatternsStore((s) => s.putPattern);
  const putList = useTypePatternsStore((s) => s.putList);
  const putCustomType = useTypePatternsStore((s) => s.putCustomType);

  const [editingType, setEditingType] = useState<string | null>(null);
  const [editingList, setEditingList] = useState(false);
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');
  const [owner, setOwner] = useState(list.ownerOrgUnitId ?? '');
  const [newType, setNewType] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function saveList() {
    setError(null);
    const { list: saved, error: err } = await saveTypeList({
      workspaceId: list.workspaceId,
      listId: list.id,
      name,
      description: description || null,
      ownerOrgUnitId: owner || null,
      isActive: list.isActive,
    });
    if (err || !saved) return setError(err ?? 'Nothing was saved.');
    putList(saved);
    setEditingList(false);
  }

  async function addType() {
    if (!newType.trim()) return;
    setError(null);
    const { type, error: err } = await saveCustomType({ listId: list.id, typeId: null, label: newType.trim(), description: null });
    if (err || !type) return setError(err ?? 'Nothing was saved.');
    putCustomType(type);
    setNewType('');
  }

  async function toggleType(t: TypeOption) {
    if (!t.id) return;
    const { type, error: err } = await saveCustomType({ listId: list.id, typeId: t.id, label: t.label, description: t.description, isActive: !t.isActive });
    if (err || !type) return setError(err ?? 'Nothing was saved.');
    putCustomType(type);
  }

  const ownerText = orgLabel(list.ownerOrgUnitId);
  const options = list.options.filter((o) => o.isActive || !list.builtin);

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5" data-testid={`type-list-${list.key}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">
            {list.name}
            {!list.isActive && <span className="ml-2 text-xs font-normal text-[var(--text-ter)]">switched off</span>}
          </h3>
          {list.description && <p className="mt-0.5 text-sm text-[var(--text-ter)]">{list.description}</p>}
          <p className="mt-1 text-xs text-[var(--text-sec)]">
            Owners: {ownerText ? <>the people under <strong>{ownerText}</strong>, anyone whose role can edit type lists, and admins</> : 'anyone whose role can edit type lists, and admins'}
          </p>
        </div>
        {isAdmin && !editingList && (
          <button type="button" onClick={() => setEditingList(true)} {...btnSecondary('flex items-center gap-1 text-xs')}>
            <Pencil className="h-3 w-3" /> Edit list
          </button>
        )}
      </div>

      {editingList && (
        <div className="mt-3 grid gap-3 border border-[var(--rule)] bg-[var(--surface)] p-4 sm:grid-cols-2">
          <Field label="Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Owners: the people under">
            <Select value={owner} placeholder="No org unit — admins only" onChange={(e) => setOwner(e.target.value)}
              options={orgUnits.map((u) => ({ value: u.id, label: orgLabel(u.id) ?? u.displayName }))} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description"><TextInput value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          </div>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <button type="button" onClick={() => setEditingList(false)} {...btnSecondary()}>Cancel</button>
            <button type="button" onClick={() => void saveList()} {...btnPrimary()}>Save list</button>
          </div>
        </div>
      )}

      {error && <p className="mt-2 text-sm text-[var(--coral)]">{error}</p>}

      <div className="mt-4 divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
        {options.length === 0 && <p className="py-3 text-sm text-[var(--text-ter)]">No types yet.</p>}
        {options.map((t) => {
          const p = patternFor(list.key, t.value);
          return (
            <div key={t.value} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <span className={`text-sm font-medium ${t.isActive ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)] line-through'}`}>{t.label}</span>
                  <span className="ml-2 text-xs text-[var(--text-ter)]">{patternSummary(p)}</span>
                </div>
                {mayEdit && editingType !== t.value && (
                  <div className="flex gap-2">
                    {!list.builtin && t.id && (
                      <button type="button" onClick={() => void toggleType(t)} {...btnSecondary('text-xs')}>
                        {t.isActive ? 'Switch off' : 'Switch on'}
                      </button>
                    )}
                    {!patternBlock && (
                      <button type="button" onClick={() => setEditingType(t.value)} {...btnSecondary('text-xs')}>
                        {p ? 'Edit pattern' : 'Add pattern'}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {editingType === t.value && (
                <PatternEditor
                  list={list}
                  type={t}
                  current={p}
                  workTypeFacts={list.key === 'work_type' ? workTypeFacts(t.value) : null}
                  resourceTypes={resourceTypes}
                  workloadTemplates={workloadTemplates}
                  onCancel={() => setEditingType(null)}
                  onSaved={(saved) => {
                    putPattern(saved);
                    setEditingType(null);
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {list.builtin ? (
        <p className="mt-3 text-xs text-[var(--text-ter)]">
          Add or rename these types on{' '}
          <Link to={BUILTIN_PAGE[list.key]?.href ?? '#'} className="underline">{BUILTIN_PAGE[list.key]?.label ?? list.name}</Link>.
        </p>
      ) : (
        mayEdit && (
          <div className="mt-3 flex items-end gap-2">
            <div className="max-w-xs flex-1">
              <TextInput size="sm" value={newType} placeholder="New type" aria-label="New type" onChange={(e) => setNewType(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void addType(); }} />
            </div>
            <button type="button" onClick={() => void addType()} {...btnSecondary('flex items-center gap-1 text-xs')}>
              <Plus className="h-3 w-3" /> Add type
            </button>
          </div>
        )
      )}
      {mayEdit && patternBlock && (
        <p className="mt-3 text-xs text-[var(--text-ter)]">Changing these patterns: {patternBlock}</p>
      )}
      {mayEdit === false && (
        <p className="mt-3 text-xs text-[var(--text-ter)]">
          Changing these patterns needs an admin, a role that can edit type lists{ownerText ? `, or a place in ${ownerText}` : ''}.
        </p>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function TypePatternsSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const can = useUserStore((s) => s.can);
  const isAdmin = can('admin.settings');

  const { lists, loading, error, workTypeFacts } = useTypeListPickers(workspaceId);
  const putList = useTypePatternsStore((s) => s.putList);

  const orgUnits = useOrgUnits();
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const loadResourceTypes = useResourceTypesStore((s) => s.load);
  const { getEnabledTypes } = useResourceTypesStore();
  const enabledTypes = getEnabledTypes();
  const [workloadTemplates, setWorkloadTemplates] = useState<LaunchProfile[]>([]);
  const [editable, setEditable] = useState<Record<string, boolean>>({});
  const [patternBlocks, setPatternBlocks] = useState<Record<string, string | null>>({});

  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;
    void fetchOrgUnits();
    void loadResourceTypes(workspaceId);
    getLaunchProfiles(workspaceId).then(setWorkloadTemplates).catch(() => setWorkloadTemplates([]));
  }, [workspaceId, fetchOrgUnits, loadResourceTypes]);

  // Who may edit each list: asked of the database, the same rule it enforces.
  const listIds = lists.map((l) => l.id).join(',');
  useEffect(() => {
    if (!userId || !listIds) return;
    let live = true;
    void Promise.all(lists.map(async (l) => [l.id, await mayEditTypeList(l.id, userId)] as const)).then((pairs) => {
      if (live) setEditable(Object.fromEntries(pairs));
    });
    // And whether a policy holds back pattern changes — the check revise_type_pattern makes.
    void Promise.all(lists.map(async (l) => [l.id, await patternPolicyBlock(l.workspaceId, l.id)] as const)).then((pairs) => {
      if (live) setPatternBlocks(Object.fromEntries(pairs));
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, listIds]);

  const wsUnits = useMemo(
    () => orgUnits.filter((u) => !u.workspaceId || u.workspaceId === workspaceId),
    [orgUnits, workspaceId],
  );
  const byId = useMemo(() => new Map(wsUnits.map((u) => [u.id, u])), [wsUnits]);
  const orgLabel = (id: string | null) => {
    if (!id) return null;
    const u = byId.get(id);
    return u ? formatOrgUnitPathLabel(u, byId) : 'an org unit';
  };
  const resourceTypes = enabledTypes.map((t) => ({ key: t.key, label: t.label }));

  async function addList() {
    if (!workspaceId || !newName.trim()) return;
    setAddError(null);
    const { list, error: err } = await saveTypeList({
      workspaceId,
      listId: null,
      name: newName.trim(),
      description: null,
      ownerOrgUnitId: newOwner || null,
    });
    if (err || !list) return setAddError(err ?? 'Nothing was saved.');
    putList(list);
    setAdding(false);
    setNewName('');
    setNewOwner('');
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <PageHeader
        title="Type lists & patterns"
        subtitle="Each department sees an item its own way. A type carries a pattern — how sure we are, how the work usually unfolds, which teams it takes and when, and what it is held to. Planning reads the patterns of an item's types."
        icon={<Layers className="h-5 w-5 text-[var(--text-ter)]" />}
      />

      {loading && lists.length === 0 && <p className="text-sm text-[var(--text-ter)]">Loading…</p>}
      {error && <p className="text-sm text-[var(--coral)]">Could not load the type lists: {error}</p>}

      <div className="space-y-4">
        {lists.map((l) => (
          <TypeListCard
            key={l.id}
            list={l}
            isAdmin={isAdmin}
            mayEdit={editable[l.id]}
            patternBlock={patternBlocks[l.id] ?? null}
            orgUnits={wsUnits}
            orgLabel={orgLabel}
            workTypeFacts={workTypeFacts}
            resourceTypes={resourceTypes}
            workloadTemplates={workloadTemplates}
          />
        ))}
      </div>

      {isAdmin && (
        <div className="mt-6">
          {adding ? (
            <div className="grid gap-3 border border-[var(--rule)] bg-[var(--bg-raised)] p-4 sm:grid-cols-2">
              <Field label="Name of the new list" hint="For example, Legal review or Support impact.">
                <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} />
              </Field>
              <Field label="Owners: the people under">
                <Select value={newOwner} placeholder="No org unit — admins only" onChange={(e) => setNewOwner(e.target.value)}
                  options={wsUnits.map((u) => ({ value: u.id, label: orgLabel(u.id) ?? u.displayName }))} />
              </Field>
              {addError && <p className="text-sm text-[var(--coral)] sm:col-span-2">{addError}</p>}
              <div className="flex justify-end gap-2 sm:col-span-2">
                <button type="button" onClick={() => setAdding(false)} {...btnSecondary()}>Cancel</button>
                <button type="button" onClick={() => void addList()} {...btnPrimary()}>Add list</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setAdding(true)} {...btnSecondary('flex items-center gap-1.5')}>
              <Plus className="h-3.5 w-3.5" /> Add a type list
            </button>
          )}
        </div>
      )}
    </div>
  );
}
